import type { ChangeBookingFlow } from '@/features/manage/manageActions';
import type {
  AdmissionDTO,
  BookedTripDTO,
  BookingDTO,
  ErrorDTO,
  PassengerDTO,
  SalesPointConfigurationResponseDTO,
} from '@/types/dto/agent';
import { isAdmissionInactive } from '@/utils/admission';
import { isNonEmptyString } from '@/utils/typeGuards';
import type { SearchFormValues } from '@packages/webshop-search-widget';

// ---------------------------------------------------------------------------
// Conflict detection — change-trips selection
// ---------------------------------------------------------------------------
//
// Background
// ----------
// A booking can end up with passengers travelling on different *physical*
// outbound (or return) trips. The typical cause: a prior modification handled
// by customer support, where one passenger out of N had their outbound
// exchanged. In the API payload this manifests as multiple BookedTripDTOs in
// the same direction, each covering a different subset of passengers (via the
// union of `bookedOffers[].admissions[].passengerIds`).
//
// When the user picks one outbound trip card AND one return trip card in the
// Change-Trips modal, those two trips may cover incompatible passenger sets:
//
//   outbound trip X covers {A, B}
//   return   trip Y covers {A, B, C}
//
// The two cannot be modified atomically because they describe different
// subsets of the booking. The user must perform two modifications (or pick
// one direction to handle first). The webshop spec calls for a
// "Conflicting trips" dialog that asks the user to choose; `hasConflictingTrips`
// returns the flag that triggers it.
//
// Relation to agent-tool
// ----------------------
// Mirrors the intent of `differentRouteSelection` in
// agent-tool/src/routes/bookingDetails/modal/ModifySelectionModal.tsx:126-174.
// Differences:
//   - agent-tool's selection unit is an admission ID, so it compares per-
//     passenger leg sets and uses a physical-leg-key map to neutralise legId
//     churn from prior exchange operations.
//   - webshop's selection unit is a BookedTripDTO. Each trip already carries
//     the passengers it covers (via its admissions); we compare those sets
//     directly. No legId-stability problem applies here.
// ---------------------------------------------------------------------------

export interface ChangeTripsSelection {
  outboundTripId: string | null;
  returnTripId: string | null;
}

// Flattens every admission on a trip across all bookedOffers. The active
// variant additionally filters out admissions in inactive states
// (EXCHANGED / REFUNDED / RELEASED / CANCELLED), which is the predicate we
// want almost everywhere we describe a trip's *current* state.
function getAllAdmissions(trip: BookedTripDTO): Array<AdmissionDTO> {
  return (trip.bookedOffers ?? []).flatMap(
    ({ admissions }) => admissions ?? []
  );
}

function getActiveAdmissions(trip: BookedTripDTO): Array<AdmissionDTO> {
  return getAllAdmissions(trip).filter(
    (admission) => !isAdmissionInactive(admission)
  );
}

// Returns the selection's non-empty trip IDs in `[outbound, return]` order.
function getSelectedTripIds(selection: ChangeTripsSelection): Array<string> {
  return [selection.outboundTripId, selection.returnTripId].filter(
    isNonEmptyString
  );
}

// Resolves the user's change-trips selection into the live BookedTripDTO
// objects from the booking. Returns trips in the order they appear in the
// booking (not the order the slots were filled), so an outbound trip and a
// return trip will come out in their natural direction order.
function getSelectedTrips(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>
): Array<BookedTripDTO> {
  const selectedIds = getSelectedTripIds(selection);
  if (selectedIds.length === 0) return [];
  return bookedTrips.filter(({ id }) => id && selectedIds.includes(id));
}

// Aggregates fulfillment IDs from the selected trips' active admissions,
// optionally narrowed to a single direction. The
// `Modifications_FindExchangeOffers` endpoint takes fulfillment IDs (one
// level below admissions) as input — they identify the actual fares being
// replaced by the search.
//
// `direction` filters trips by `isOutbound`:
//   - `true`  → only outbound trip fulfillments
//   - `false` → only return trip fulfillments
//   - omitted → all selected trips
export function getSelectedFulfillmentIds(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>,
  direction?: boolean
): Array<string> {
  return getSelectedTrips(selection, bookedTrips)
    .filter((trip) => direction === undefined || trip.isOutbound === direction)
    .flatMap(getActiveAdmissions)
    .flatMap(({ fulfillments }) => fulfillments ?? [])
    .map(({ id }) => id)
    .filter(isNonEmptyString);
}

// Aggregates admission IDs across both selected trips, excluding admissions
// whose status is inactive (EXCHANGED / REFUNDED / RELEASED / CANCELLED).
// Useful when an action (refund / cancel / exchange) needs to be initiated
// against the union of fulfillment-level entities the user has selected —
// inactive admissions are never valid targets for those operations.
export function getSelectedAdmissionIds(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>
): Array<string> {
  return getSelectedTrips(selection, bookedTrips)
    .flatMap(getActiveAdmissions)
    .map(({ id }) => id)
    .filter(isNonEmptyString);
}

// Aggregates the union of active passenger IDs across the user's selected
// trips. After conflict resolution both trips share the same passenger set,
// so the union equals either side's set. When only one direction is selected
// the union equals that trip's set. Empty array when nothing is selected.
//
// This is the canonical "scope of the modification" — which passengers the
// New Search and downstream exchange API call apply to.
export function getSelectedPassengerIds(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>
): Array<string> {
  return Array.from(
    new Set(
      getSelectedTrips(selection, bookedTrips).flatMap(collectTripPassengerIds)
    )
  );
}

// Same scope as `getSelectedPassengerIds`, resolved into the PassengerDTO
// objects so callers get the booking's own `externalReference` / `type` /
// `age` without re-walking `booking.passengers`.
//
// Modification endpoints must be fed from here, never from search-form state:
// the form's passengers belong to the search widget, which mints its own
// external references, and a reference that isn't on the booking makes the BE
// reject the request.
export function getSelectedPassengers(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>,
  passengers: ReadonlyArray<PassengerDTO>
): Array<PassengerDTO> {
  const selectedIds = new Set(getSelectedPassengerIds(selection, bookedTrips));
  return passengers.filter(({ id }) => id && selectedIds.has(id));
}

// Returns true if every admission on the trip has an inactive status (or the
// trip has no admissions at all). Such trips are visual no-ops in the
// Change-Trips modal: the user can't move anyone off them and shouldn't see
// them offered as a target. Mirrors agent-tool's `getTripAdmissions(trip)
// .every(isAdmissionInactive)` filter in `agent-tool/src/utils/trip.ts:465`.
export function isTripWhollyInactive(trip: BookedTripDTO): boolean {
  return getAllAdmissions(trip).every(isAdmissionInactive);
}

// Resolves the passenger IDs covered by a trip into the corresponding
// PassengerDTO objects from the booking. Useful for surfacing names / refs
// of the passengers travelling on a given trip without re-walking admissions
// at the call site.
export function getTripPassengers(
  trip: BookedTripDTO,
  passengers: ReadonlyArray<PassengerDTO>
): Array<PassengerDTO> {
  const tripPassengerIds = new Set(collectTripPassengerIds(trip));
  return passengers.filter(({ id }) => id && tripPassengerIds.has(id));
}

// Collects the unique promo-code strings that were applied to the user's
// selected trip(s). Walks every active admission and pulls
// `appliedReductions` where `type === 'PROMO'`. The same code may appear
// on multiple admissions (e.g. once per passenger) — we dedupe.
//
// Returned strings are RAW codes only. The widget's form needs
// `{ code, name, description }` entries, so consumers should re-validate
// each code via the agent API to obtain the metadata before seeding the
// form. See `useValidatedChangeBookingPromoCodes` for that workflow.
export function getBookingPromoCodeStrings(
  trips: ReadonlyArray<BookedTripDTO>
): Array<string> {
  const codes = new Set<string>();
  for (const trip of trips) {
    for (const admission of getActiveAdmissions(trip)) {
      for (const reduction of admission.appliedReductions ?? []) {
        if (reduction.type !== 'PROMO') continue;
        if (reduction.code) codes.add(reduction.code);
      }
    }
  }
  return Array.from(codes);
}

// Builds the full Partial<SearchFormValues> we want to seed when the
// customer enters the search widget via the Change-Trips flow. Covers:
//   - origin/destination — from the first selected trip's endpoints
//   - departureDate      — from the first selected trip's departureTime
//   - passengers         — fixed roster of the booking (see
//                          `buildSearchFormPassengers`)
//   - promoCodes         — the validated rehydration of the booking's
//                          original promo codes (caller supplies the
//                          validated array; see
//                          `useValidatedChangeBookingPromoCodes`)
//
// Caller composes against the user's current form state — typically as
// `{ ...currentSearchFormValues, ...buildChangeBookingSearchFormValues(...) }`.
//
// Returns an empty object when no trip is selected; consumers should fall
// back to the regular new-purchase form-values flow in that case.
export function buildChangeBookingSearchFormValues(
  selectedPassengers: ReadonlyArray<PassengerDTO>,
  selectedTrips: ReadonlyArray<BookedTripDTO>,
  posConfigPassengerTypes:
    | SalesPointConfigurationResponseDTO['passengerTypes']
    | undefined,
  validatedPromoCodes: SearchFormValues['promoCodes']
): Partial<SearchFormValues> {
  const trip = selectedTrips[0];
  if (!trip) return {};

  const passengers = buildSearchFormPassengers(
    selectedPassengers,
    trip,
    posConfigPassengerTypes
  );

  const partial: Partial<SearchFormValues> = {
    // Change-booking is currently scoped to ticket bookings — travel-pass
    // modifications go through a different flow entirely. Hardcode this
    // so the auto-submitted search request always carries a valid value.
    productType: 'ticket',
    passengers,
    promoCodes: validatedPromoCodes,
  };

  if (trip.originStop?.code && trip.originStop.name) {
    partial.origin = {
      value: trip.originStop.code,
      type: 'stop',
      label: trip.originStop.name,
      cityName: trip.originStop.cityName || trip.originStop.name,
    };
  }
  if (trip.destinationStop?.code && trip.destinationStop.name) {
    partial.destination = {
      value: trip.destinationStop.code,
      type: 'stop',
      label: trip.destinationStop.name,
      cityName: trip.destinationStop.cityName || trip.destinationStop.name,
    };
  }
  if (trip.departureTime) {
    partial.departureDate = new Date(trip.departureTime);
  }

  // Round-trip change: when the user selected both directions (and the
  // primary trip is the outbound), set `returnDate` from the return
  // leg's departure time so the search widget renders a round-trip
  // form and OutboundJourneyList also dispatches the inbound search.
  // Inbound-only selections (no outbound) stay as one-way searches —
  // `trip` is already the inbound in that case.
  const returnTrip = trip.isOutbound ? selectedTrips[1] : undefined;
  if (returnTrip?.departureTime) {
    partial.returnDate = new Date(returnTrip.departureTime);
  }
  return partial;
}

// Derives a plausible age for a passenger from their POS passenger type's
// validity criteria. The search widget's schema requires an age for every
// non-default passenger type (age-based search tenants), but booking
// passengers frequently carry no age — and in the change-booking flow the
// passenger picker is locked, so the customer cannot supply one. Any age
// inside the type's [ageFrom, ageTo] window resolves back to the same
// passenger type, so a synthetic in-range value keeps the search valid
// without changing which fares apply.
//
// The midpoint is clamped to ageFrom + 10 so open-ended ranges (e.g. adult
// 18–999) yield a sane value (28) instead of the literal midpoint (508).
function deriveAgeFromPassengerType(
  posType: NonNullable<
    SalesPointConfigurationResponseDTO['passengerTypes']
  >[number]
): number | undefined {
  const criteria = posType.validityCriterias?.[0];
  if (
    typeof criteria?.ageFrom !== 'number' ||
    typeof criteria?.ageTo !== 'number'
  ) {
    return undefined;
  }
  return Math.min(
    Math.floor((criteria.ageFrom + criteria.ageTo) / 2),
    criteria.ageFrom + 10
  );
}

// Bridges a change-booking selection into the search widget's
// `passengers` form-value shape. Used to prefill (and, paired with
// `lockedFields.passengers`, lock) the passenger picker when the
// customer arrives at /purchase in booking-modification mode.
//
// The mapping is:
//   booking passenger.id
//     → trip's appliedPassengerType.type (e.g. "ADULT")
//     → POS config passengerType.passengerTypeCategory.id
//       (e.g. "PASSENGER_TYPE_CATEGORY.ADULT")
//     → POS config passengerType.id (numeric, what the form needs)
//
// The POS lookup only *enriches* the result; it never decides who is in it. A
// passenger whose type code resolves to no POS entry — including every
// passenger on a tenant whose POS configuration carries no `passengerTypes` —
// still comes out, just without `passengerTypeId` (optional in the widget's
// schema) and without a derived age. Dropping them would empty the roster and
// leave the widget's own default passenger in the form.
export function buildSearchFormPassengers(
  selectedPassengers: ReadonlyArray<PassengerDTO>,
  trip: BookedTripDTO | undefined,
  posConfigPassengerTypes:
    | SalesPointConfigurationResponseDTO['passengerTypes']
    | undefined
): SearchFormValues['passengers'] {
  if (!trip) return [];

  const typeCodeByPassengerId = getPassengerTypeCodeByPassengerId(trip);
  const posTypeByCategoryCode = new Map<
    string,
    NonNullable<SalesPointConfigurationResponseDTO['passengerTypes']>[number]
  >();
  for (const posType of posConfigPassengerTypes ?? []) {
    const fullId = posType.passengerTypeCategory?.id;
    if (!fullId) continue;
    // e.g. "PASSENGER_TYPE_CATEGORY.ADULT" → "ADULT"
    const code = fullId.replace(/^PASSENGER_TYPE_CATEGORY\./, '');
    posTypeByCategoryCode.set(code, posType);
  }

  return selectedPassengers.flatMap<SearchFormValues['passengers'][number]>(
    (passenger) => {
      if (!passenger.id || !passenger.externalReference) return [];
      const typeCode = typeCodeByPassengerId[passenger.id];
      const matchedType = typeCode
        ? posTypeByCategoryCode.get(typeCode)
        : undefined;

      if (!matchedType) {
        return [
          {
            externalReference: passenger.externalReference,
            type: 'PERSON',
            isDefault: false,
            ...(passenger.age != null ? { age: passenger.age } : {}),
          },
        ];
      }

      const age = passenger.age ?? deriveAgeFromPassengerType(matchedType);

      return [
        {
          passengerTypeId: matchedType.id,
          externalReference: passenger.externalReference,
          type: 'PERSON',
          isDefault: matchedType.isDefault,
          ...(age != null ? { age } : {}),
        },
      ];
    }
  );
}

// Builds a map of `passenger.id → applied passenger type CODE` (e.g.
// "ADULT") for a single trip. Use when you need the structured type
// identifier to match against another system (e.g. mapping to the
// numeric `passengerTypeId` in the POS configuration). For display
// purposes prefer `buildPassengerTypeByPassengerId` which returns the
// localised label.
export function getPassengerTypeCodeByPassengerId(
  trip: BookedTripDTO
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const admission of getActiveAdmissions(trip)) {
    for (const appliedType of admission.appliedPassengerTypes ?? []) {
      const ref = appliedType.passengerRef;
      const code = appliedType.type;
      if (ref && code) {
        result[ref] = code;
      }
    }
  }
  return result;
}

// Builds a map of `passenger.id → applied passenger type label` for a single
// trip. The `appliedPassengerTypes` array lives on each admission; despite
// the field being called `passengerRef`, its value corresponds to
// `passenger.id` from `booking.passengers`, not `passenger.externalReference`.
// (Verified empirically against a live booking — the API field name is a
// historical misnomer.)
//
// Inactive admissions are skipped so a passenger who has been moved off this
// trip via an EXCHANGE doesn't poison the map with a stale type. The label
// prefers `description` (typically localised, e.g. "Adult") and falls back to
// the structured `type` code (e.g. "ADULT") when description is absent.
export function buildPassengerTypeByPassengerId(
  trip: BookedTripDTO
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const admission of getActiveAdmissions(trip)) {
    for (const appliedType of admission.appliedPassengerTypes ?? []) {
      const ref = appliedType.passengerRef;
      const label = appliedType.description ?? appliedType.type;
      if (ref && label) {
        result[ref] = label;
      }
    }
  }
  return result;
}

export interface TripOfferRow {
  label: string;
  isExchangeable: boolean;
}

// Groups a trip's active admissions by their product label (productSummary,
// falling back to summary) so each distinct offer renders as its own row in
// the trip card's product/changeability section. Inactive admissions are
// excluded — they represent fare lines that no longer apply to this trip.
//
// When the resulting array has more than one entry the trip has "mixed
// offers" (different passengers on different fares on the same physical
// trip). Per QA spec, that combination is not changeable through the
// storefront; the consuming component should disable the card and require
// the user to contact customer support to mutate it through agent-tool.
export function getTripOfferRows(trip: BookedTripDTO): Array<TripOfferRow> {
  // Map preserves insertion order, which is exactly the order we want for
  // rendering (first occurrence of each label wins its slot).
  const rowsByLabel = new Map<string, TripOfferRow>();

  for (const admission of getActiveAdmissions(trip)) {
    const label = admission.productSummary ?? admission.summary ?? '';
    const isExchangeable = admission.exchangeable === 'YES';
    const existing = rowsByLabel.get(label);
    if (existing) {
      existing.isExchangeable = existing.isExchangeable && isExchangeable;
    } else {
      rowsByLabel.set(label, { label, isExchangeable });
    }
  }

  return Array.from(rowsByLabel.values());
}

// Returns true if a trip cannot be modified through the storefront — either
// because it carries a mixed-offer combination (different fares for different
// passengers on the same physical trip) or because at least one active
// admission is non-exchangeable. Consumers use this to disable the card and
// to apply dimmed styling to surrounding chrome (Trip N label, passenger
// popover trigger).
export function isTripChangeDisabled(
  trip: BookedTripDTO,
  changeFlow: ChangeBookingFlow
): boolean {
  const offerRows = getTripOfferRows(trip);
  if (changeFlow === 'changeTravelClass') {
    // Change travel class flow allows multileg upgrades,
    // just make sure all offers are exchangeable.
    return (
      !offerRows.length ||
      offerRows.length > offerRows.filter((row) => row.isExchangeable).length
    );
  }
  if (offerRows.length !== 1) return true;
  return !offerRows[0].isExchangeable;
}

// Collects the union of passengerIds across all *active* admissions on a
// booked trip. Inactive admissions (status EXCHANGED / REFUNDED / RELEASED /
// CANCELLED) are excluded — they represent passengers who have been moved
// off this trip or whose admission has been terminated, so they should not
// count towards the trip's current roster. See `isAdmissionInactive` in
// `@/utils/admission` for the canonical inactive set.
//
// The union (rather than any single admission's list) is what matters because
// one bookedOffer can have multiple admissions (multi-leg or split fares),
// and a single physical trip can carry multiple offers (mixed-offer trips,
// e.g. different fare groups per passenger group on the same trip).
export function collectTripPassengerIds(trip: BookedTripDTO): Array<string> {
  const passengerIds = getActiveAdmissions(trip).flatMap(
    ({ passengerIds: ids }) => ids ?? []
  );
  return Array.from(new Set(passengerIds));
}

// Canonical, comparable representation of a trip's passenger set. Two trips
// cover the same passengers iff their canonical keys are string-equal.
export function passengerSetKey(trip: BookedTripDTO): string {
  return collectTripPassengerIds(trip).sort().join('|');
}

// Builds a `tripId → passengerSetKey` lookup over the whole booking. We index
// every trip (not just the selected pair) so the conflict check itself is a
// single map lookup + string compare, and so a future consumer could reuse
// the same map cheaply.
export function buildPassengerKeyByTripId(
  bookedTrips: ReadonlyArray<BookedTripDTO>
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const trip of bookedTrips) {
    if (!trip.id) continue;
    result[trip.id] = passengerSetKey(trip);
  }
  return result;
}

// Enumerates every reason an alert can be shown during a change-trips or
// change-travel-class flow. Local validators (e.g. detectConflictReason) and
// API error codes both map onto this enum. String values are UPPER_SNAKE_CASE
// API error codes; i18n mapping lives in ALERT_CONTENT inside ChangeTripsModalAlert.
export enum ALERT_REASON {
  // Detected locally by detectConflictReason.
  CONFLICTING_TRIPS = 'CONFLICTING_TRIPS',
  // Returned by the API. Also set synthetically when startExchange returns
  // INTERNAL_ERROR but a re-fetch still finds available offers — the only
  // reason that shows a "Continue" button so the user can pick the new offer.
  SELECTED_TICKET_JUST_SOLD_OUT = 'SELECTED_TICKET_JUST_SOLD_OUT',
  // Returned by the API (caught by getChangeTravelClassOffers / startExchange).
  SOLD_OUT = 'SOLD_OUT',
  UPGRADE_BLOCKED_BY_RESTRICTIVE_ANCILLARY = 'UPGRADE_BLOCKED_BY_RESTRICTIVE_ANCILLARY',
}

export interface AlertReason {
  code: ALERT_REASON;
  message: string;
  instance?: string;
}

const ALERT_REASON_CODES = new Set<string>(Object.values(ALERT_REASON));

// Converts BE business-error codes into alert modal state; unknown codes keep
// falling through to the generic toast handling.
export const getAlertReasonFromApiError = (
  error?: ErrorDTO
): AlertReason | null => {
  if (!error?.code || !ALERT_REASON_CODES.has(error.code)) {
    return null;
  }
  return {
    code: error.code as ALERT_REASON,
    message: error.message ?? '',
    instance: error.instance,
  };
};

// Orchestrates every conflict validator in order and returns the first hit
// (or null when the selection is valid). Consumers should call this rather
// than the individual predicates so adding a new validator is a one-line
// change at this central point.
export function detectConflictReason(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>
): ALERT_REASON | null {
  if (hasConflictingTrips(selection, bookedTrips)) {
    return ALERT_REASON.CONFLICTING_TRIPS;
  }
  return null;
}

// Returns true when the user's outbound + return selections cover different
// passenger sets. Returns false when fewer than two slots are filled, when
// both selected trips share the same passenger set, or when a selected ID
// fails to resolve against the supplied booking (defensive — a stale
// selection might survive a refetch for a tick; treat that as "no conflict"
// rather than throwing).
//
// Uses a Set over the resolved passenger-set keys so the same code
// generalises trivially if a future iteration supports more than two
// simultaneous direction slots (e.g. open-jaw bookings) without restructuring.
export function hasConflictingTrips(
  selection: ChangeTripsSelection,
  bookedTrips: ReadonlyArray<BookedTripDTO>
): boolean {
  const selectedTripIds = getSelectedTripIds(selection);
  if (selectedTripIds.length < 2) return false;

  const passengerKeyByTripId = buildPassengerKeyByTripId(bookedTrips);
  const selectedKeys = selectedTripIds
    .map((id) => passengerKeyByTripId[id])
    .filter(isNonEmptyString);

  if (selectedKeys.length < 2) return false;
  return new Set(selectedKeys).size > 1;
}

// API errors may point at a concrete booking resource via `instance`, e.g.
// `ancillary/{ancillaryId}`. Only ancillary instances are relevant here.
export const getAncillaryIdFromInstance = (
  instance?: string
): string | null => {
  const match = instance?.match(/^ancillary\/(.+)$/);
  return match?.[1] ?? null;
};

// Booked ancillaries live under each booked offer admission, so resolving an
// API instance requires walking the current booking's trip/offer/admission tree.
export const getAncillaryName = (
  booking: BookingDTO | null | undefined,
  ancillaryId: string | null
): string | null => {
  if (!ancillaryId) return null;

  for (const trip of booking?.bookedTrips ?? []) {
    for (const offer of trip.bookedOffers ?? []) {
      for (const admission of offer.admissions ?? []) {
        const ancillary = admission.ancillaries?.find(
          ({ id }) => id === ancillaryId
        );
        if (ancillary) {
          return (
            ancillary.productDescription || ancillary.productSummary || null
          );
        }
      }
    }
  }

  return null;
};
